import { Card, Row, Col, Tag, Divider } from "antd";
import { Link } from "react-router-dom";
import "../../../assets/css/dashboard.css";
import { useTranslation } from "react-i18next";

const UpgradePlanCard = ({ user, analytics }) => {
  const { t } = useTranslation();

  const dateFormat = {
    year: "numeric",
    month: "long",
    day: "numeric",
  };

  return (
    <Card
      style={{
        background: "white",
        border: "1px solid #e8e8e8",
        borderRadius: "12px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
        overflow: "hidden",
      }}
      bodyStyle={{ padding: "32px" }}
    >
      {/* Header */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "24px"
      }}>
        <div>
          <h3 style={{
            color: "#262626",
            fontSize: "20px",
            fontWeight: "600",
            margin: "0 0 4px 0"
          }}>
            {t("dashboard.currentPlan")}
          </h3>
          <p style={{
            color: "#8c8c8c",
            fontSize: "14px",
            margin: 0
          }}>
            {t("dashboard.currentPlanDesc")}
          </p>
        </div>
        
        <div style={{ textAlign: "right" }}>
          <div style={{
            fontSize: "24px",
            fontWeight: "600",
            color: "#8B5CF6",
            marginBottom: "4px"
          }}>
            {analytics?.usageMetrics?.currentPlan}
          </div>
          <Tag 
            style={{
              background: analytics?.usageMetrics?.subscriptionStatus === "ACTIVE" 
                ? "#f6ffed"
                : "#fff2e8",
              border: analytics?.usageMetrics?.subscriptionStatus === "ACTIVE"
                ? "1px solid #b7eb8f"
                : "1px solid #ffbb96",
              color: analytics?.usageMetrics?.subscriptionStatus === "ACTIVE"
                ? "#52c41a"
                : "#fa8c16",
              fontSize: "12px",
              fontWeight: "500",
              borderRadius: "4px"
            }}
          >
            {analytics?.usageMetrics?.subscriptionStatus?.toUpperCase()}
          </Tag>
          {user?.appSumoUser && (
            <Tag
              style={{
                background: "#fff7e6",
                border: "1px solid #ffd591",
                color: "#fa8c16",
                marginLeft: "8px",
                fontSize: "12px",
                fontWeight: "500",
                borderRadius: "4px"
              }}
            >
              {t("dashboard.lifetime")}
            </Tag>
          )}
        </div>
      </div>

      <Divider style={{ margin: "16px 0" }} />

      {/* Credits Section */}
      <Row gutter={32} style={{ marginBottom: "24px" }}>
        <Col span={8}>
          <div>
            <p style={{
              color: "#8c8c8c",
              fontSize: "12px",
              margin: "0 0 8px 0",
              textTransform: "uppercase",
              letterSpacing: "0.5px"
            }}>
              {t("dashboard.creditsAvailable")}
            </p>
            <div style={{
              fontSize: "32px",
              fontWeight: "600",
              color: "#52c41a",
              lineHeight: 1
            }}>
              {analytics?.usageMetrics?.creditsLeft?.toLocaleString() || 0}
            </div>
          </div>
        </Col>

        <Col span={8}>
          <div>
            <p style={{
              color: "#8c8c8c",
              fontSize: "12px",
              margin: "0 0 8px 0",
              textTransform: "uppercase",
              letterSpacing: "0.5px"
            }}>
              {t("dashboard.creditsUsedLabel")}
            </p>
            <div style={{
              fontSize: "32px",
              fontWeight: "600",
              color: "#262626",
              lineHeight: 1
            }}>
              {analytics?.usageMetrics?.creditsUsed?.toLocaleString() || 0}
            </div>
          </div>
        </Col>

        <Col span={8}>
          <div>
            <p style={{
              color: "#8c8c8c",
              fontSize: "12px",
              margin: "0 0 8px 0",
              textTransform: "uppercase",
              letterSpacing: "0.5px"
            }}>
              {t("dashboard.extraCredits")}
            </p>
            <div style={{
              fontSize: "32px",
              fontWeight: "600",
              color: "#ff4d4f",
              lineHeight: 1
            }}>
              {(() => {
                const extra = (analytics?.usageMetrics?.creditsUsed || 0) - (analytics?.usageMetrics?.creditsAllotted || 0);
                return extra > 0 ? extra.toLocaleString() : 0;
              })()}
            </div>
          </div>
        </Col>
      </Row>

      {/* Progress Bar */}
      <div style={{ marginBottom: "24px" }}>
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "8px"
        }}>
          <span style={{ fontSize: "12px", color: "#8c8c8c" }}>
            {t("dashboard.usageProgress")}
          </span>
          <span style={{ fontSize: "12px", color: "#8c8c8c" }}>
            {Math.round(((analytics?.usageMetrics?.creditsUsed || 0) / (analytics?.usageMetrics?.creditsAllotted || 1)) * 100)}%
          </span>
        </div>
        <div style={{
          width: "100%",
          height: "8px",
          background: "#f0f0f0",
          borderRadius: "4px",
          overflow: "hidden"
        }}>
          <div style={{
            width: `${Math.min(100, Math.round(((analytics?.usageMetrics?.creditsUsed || 0) / (analytics?.usageMetrics?.creditsAllotted || 1)) * 100))}%`,
            height: "100%",
            background: analytics?.usageMetrics?.creditsUsed > analytics?.usageMetrics?.creditsAllotted
              ? "#ff4d4f"
              : "linear-gradient(135deg, #8B5CF6, #EC4899)",
            transition: "width 0.3s ease"
          }} />
        </div>
      </div>

      <Divider style={{ margin: "16px 0" }} />

      {/* Billing Information */}
      <Row gutter={32} style={{ marginBottom: "24px" }}>
        <Col span={12}>
          <div>
            <p style={{
              color: "#8c8c8c",
              fontSize: "12px",
              margin: "0 0 4px 0"
            }}>
              {t("dashboard.billingCycleStarted")}
            </p>
            <p style={{
              color: "#262626",
              fontSize: "14px",
              fontWeight: "500",
              margin: 0
            }}>
              {analytics?.usageMetrics?.startDate
                ? new Date(analytics.usageMetrics.startDate).toLocaleDateString("en-US", dateFormat)
                : t("dashboard.notAvailable")}
            </p>
          </div>
        </Col>

        <Col span={12}>
          <div>
            <p style={{
              color: "#8c8c8c",
              fontSize: "12px",
              margin: "0 0 4px 0"
            }}>
              {user?.appSumoUser ? t("dashboard.nextAllocation") : t("dashboard.nextBilling")}
            </p>
            <p style={{
              color: "#262626",
              fontSize: "14px",
              fontWeight: "500",
              margin: 0
            }}>
              {analytics?.usageMetrics?.endDate
                ? new Date(analytics.usageMetrics.endDate).toLocaleDateString("en-US", dateFormat)
                : t("dashboard.notAvailable")}
            </p>
          </div>
        </Col>
      </Row>

      {/* Action Button */}
      <Link
        to="/profile/subscription"
        style={{
          background: "linear-gradient(135deg, #8B5CF6, #EC4899)",
          color: "white",
          borderRadius: "6px",
          padding: "12px 24px",
          textDecoration: "none",
          fontWeight: "600",
          textAlign: "center",
          display: "block",
          fontSize: "14px",
          transition: "all 0.3s ease",
          boxShadow: "0 4px 14px rgba(139, 92, 246, 0.35)"
        }}
        onMouseEnter={(e) => {
          e.target.style.background = "linear-gradient(135deg, #7C3AED, #DB2777)";
          e.target.style.boxShadow = "0 6px 20px rgba(139, 92, 246, 0.45)";
        }}
        onMouseLeave={(e) => {
          e.target.style.background = "linear-gradient(135deg, #8B5CF6, #EC4899)";
          e.target.style.boxShadow = "0 4px 14px rgba(139, 92, 246, 0.35)";
        }}
      >
        {t("dashboard.manageSubscription")}
      </Link>
    </Card>
  );
};

export default UpgradePlanCard;